import React from 'react'
import { isMobile } from 'react-device-detect'
import ProfileSection from 'app/common/pixel-menu/_comp/ProfileSection'

import { getAllUsers } from 'client/utils/api-routes-admin'
import {
  getInventoryCycles,
  createInventoryCycle,
  addProductToCycle,
  getInventoryCycleDetails,
  getWarehouses,
  validateProducts,
  approveInventoryCycle,
  updateInventoryCycleDetail,
} from 'client/utils/api-routes-inventory'
import CheckIcon from 'client/utils/icons/check-icon'
import CloseIcon from 'client/utils/icons/close-icon'
import FilterLinesIcon from 'client/utils/icons/filter-lines-icon'
import PenIcon from 'client/utils/icons/pen-icon'

import { SaveIcon } from 'client/utils/icons/save-icon'
import { ScanIcon } from 'client/utils/icons/scan-icon'
import _ from 'lodash'
import {
  DcFlexBox,
  DcButton,
  DcInput,
  DcDropDown,
  PixelText,
  PixelImage,
  DcBreadcrumbs,
  Section,
  SectionContent,
  PixelPageContainer,
  PixelTable,
  DcOverlaySlider,
  DcDiv,
  IPixelFactory,
  PixelFactoryContext,
  colors,
  DcTag,
  PixelDate,
} from 'pixel-base'
import DcScanner from 'pixel-base/components/dc-scanner'
import queryString from 'query-string'
import { useApi } from 'shared/hooks/useApi'
import InventoryCycleDetail from './InventoryCycleDetail'
import {
  IInventoryCycle,
  IWarehouse,
  IInventoryCycleResponse,
  IInventoryLifecycleFilters,
  IInventoryCycleDetailResponse,
  IInventoryCycleDetail,
} from '../../../../../../decl'
// QuaggaJS type declarations
declare global {
  interface Window {
    Quagga: any
  }
}

const breadcrumbs = [
  {
    name: 'Inventory',
    link: '/inventory',
  },
  {
    name: 'Inventory Cycles',
    active: true,
  },
]

const InventoryLifecycle = () => {
  const pixelFactory = React.useContext<IPixelFactory>(PixelFactoryContext)

  const [inventoryCycles, setInventoryCycles] = React.useState<
    IInventoryCycle[]
  >([])
  const [warehousesList, setWarehousesList] = React.useState<IWarehouse[]>([])
  const [detailLoading, setDetailLoading] = React.useState(false)
  const [showFilters, setShowFilters] = React.useState(false)
  const [loading, setLoading] = React.useState(false)

  const [cycleData, setCycleData] = React.useState<{
    warehouseId: number | null
    storeId: string
  }>({
    warehouseId: null,
    storeId: '',
  })
  const [totalRows, setTotalRows] = React.useState(0)
  const [tableLoading, setTableLoading] = React.useState(false)
  const [scannerModal, setScannerModal] = React.useState(false)
  const [cycleModal, setCycleModal] = React.useState(false)
  const [detailSlider, setDetailSlider] = React.useState(false)
  const [selectedCycle, setSelectedCycle] = React.useState(false)
  const [cycleId, setCycleId] = React.useState<number | null>(null)
  const [cycleDetailData, setCycleDetailData] = React.useState<{
    info: IInventoryCycleDetailResponse
    data: IInventoryCycleDetail[]
  }>({
    info: null,
    data: [],
  })

  // New state for tracking current cycle
  const [currentCycleId, setCurrentCycleId] = React.useState<number | null>(
    null,
  )

  const [currentCycleStatus, setCurrentCycleStatus] = React.useState<string>('')
  const [
    currentCycleTotalWarehouseProducts,
    setCurrentCycleTotalWarehouseProducts,
  ] = React.useState<number>(0)
  const [selectedApprover, setSelectedApprover] = React.useState<number | null>(
    null,
  )
  const [users, setUsers] = React.useState<
    Array<{ id: number; name: string; email: string }>
  >([])
  const [scannedProducts, setScannedProducts] = React.useState<
    Array<{
      product_sku: string
      physical_count: number
      isValid: boolean
      errorMessage?: string
      product_id?: number
      product_name?: string
      product_image?: string | null
      countProcessed?: boolean | 'processing' | 'failed'
      pixelone_count?: number
      difference?: number
      reason?: string
      detail_id?: number
      isEditing?: boolean
    }>
  >([])

  const [canStartCycle, setCanStartCycle] = React.useState(false)
  // Scanner states
  const [scannedSKU, setScannedSKU] = React.useState('')

  // Add keyboard event listener for closing scanner modal with Enter key
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && scannerModal) {
        setScannerModal(false)
      }
    }

    if (scannerModal) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [scannerModal])

  // Scanner functionality
  const handleScan = async (sku: string) => {
    await handleScanSKU(sku)
    // Scanner modal will be closed by handleScanSKU after successful validation
  }

  //create callback function for setDetailLoading
  const handleSetDetailLoading = (loading: boolean) => {
    setDetailLoading(loading)
  }

  const p = queryString.parse(window.location.search)
  const [filters, setFilters] = React.useState<IInventoryLifecycleFilters>({
    warehouse_id: (p.warehouse_id as number) || null,
    status: (p.status as string) || 'all',
    search: (p.search as string) || '',
    page: Number(p.page) || 0,
    page_size: Number(p.page_size) || 50,
  })

  // Load warehouses from API
  useApi(() => getWarehouses(), {
    onSuccess: (response: any) => {
      setWarehousesList(response.data)

      // Auto-select the default warehouse if none is selected
      if (response.data && response.data.length > 0 && !cycleData.warehouseId) {
        const defaultWarehouse = response.data.find(
          (warehouse: IWarehouse) => warehouse.is_default,
        )
        if (defaultWarehouse) {
          setCycleData((prev) => ({
            ...prev,
            warehouseId: defaultWarehouse.wh_id,
          }))
        }
      }
    },
    onError: (error) => {
      console.error('Failed to load warehouses:', error)
    },
  })

  // Load users from API
  useApi(() => getAllUsers(), {
    onSuccess: (response: any) => {
      if (response && response.data) {
        setUsers(
          response.data.map((user: any) => ({
            id: user.id,
            name: user.name || user.username || user.email,
            email: user.email,
          })),
        )
      }
    },
    onError: (error) => {
      console.error('Failed to load users:', error)
    },
  })

  const loadInventoryCycles = async () => {
    try {
      setTableLoading(true)
      const apiFilters = {
        warehouse_id: filters.warehouse_id || null,
        status: filters.status === 'all' ? '' : filters.status,
        page: filters.page,
        page_size: filters.page_size,
      }
      const filteredFilters = Object.fromEntries(
        Object.entries(apiFilters).filter(([_, value]) => value !== null),
      )

      const response = await getInventoryCycles(filteredFilters).toPromise()
      if (response && response.response) {
        const data: IInventoryCycleResponse =
          response.response as IInventoryCycleResponse
        setInventoryCycles(data.data)
        setTotalRows(data._meta.total)
      }
    } catch (error) {
      pixelFactory.showFailure('something went wrong')
    } finally {
      setTableLoading(false)
    }
  }
  // Load inventory cycles from API
  React.useEffect(() => {
    loadInventoryCycles()
  }, [filters])

  const handleViewDetails = (row: any) => {
    const cycle = row || inventoryCycles.find((c) => c.id === row?.id)
    if (cycle) {
      setCycleId(cycle.id)
      setCycleDetailData((prev) => ({
        ...prev,
        info: cycle as any,
      }))
      setSelectedCycle(true)
      setDetailSlider(true)
    }
  }

  const handleCloseDetail = () => {
    setDetailSlider(false)
    setSelectedCycle(null)
  }

  const handleApproveCycle = async (cycleId: number) => {
    pixelFactory.pixelConfirm({
      title: 'Approve Inventory Cycle',
      message:
        'Are you sure you want to approve this inventory cycle? This action cannot be undone.',
      onConfirm: async () => {
        try {
          pixelFactory.showPageLoader()
          const response = await approveInventoryCycle(
            { status: 'completed' },
            cycleId,
          ).toPromise()
          if (response && response.response?.message) {
            pixelFactory.showSuccess(
              (response.response as any).message ||
                'Cycle approved successfully',
            )
            setDetailSlider(false)
          } else {
            pixelFactory.showFailure(
              (response.response as any).error.error ||
                'Failed to approve cycle',
            )
          }
          //update the inventory cycle object key status to completed
          setInventoryCycles((prev) =>
            prev.map((cycle) =>
              cycle.id === cycleId ? { ...cycle, status: 'completed' } : cycle,
            ),
          )
        } catch (error) {
          console.error('Failed to approve cycle:', error)
          console.error(
            'Failed to approve inventory cycle. Please try again.',
            error,
          )
        } finally {
          pixelFactory.hidePageLoader()
        }
      },
    })
  }

  const handleRejectCycle = async (cycleId: number) => {
    try {
      handleCloseDetail()
    } catch (error) {
      console.error('Failed to reject cycle:', error)
    }
  }

  // Remove invalid product from scanned list
  const handleRemoveInvalidProduct = (sku: string) => {
    setScannedProducts((prev) =>
      prev.filter((product) => product?.product_sku !== sku),
    )
  }

  // Scanner workflow functions
  const handleScanSKU = async (sku: string) => {
    // Check if warehouse is selected
    if (!cycleData.warehouseId) {
      pixelFactory.showFailure(
        'Please select a warehouse first before scanning products.',
      )
      return
    }

    // Check if SKU is already scanned
    const existingProduct = scannedProducts.find(
      (product) => product?.product_sku === sku,
    )
    if (existingProduct) {
      pixelFactory.showFailure(
        `Product with SKU ${sku} has already been scanned.`,
      )
      return
    }

    try {
      // Immediately validate the scanned SKU
      const validationData = {
        product_skus: [sku],
        store_id: cycleData.warehouseId,
      }

      const response = await validateProducts(validationData).toPromise()

      if (response && response.response) {
        const results = response.response
        const validProducts = results?.valid_products || []
        const invalidProducts =
          results.data
            ?.map((item: any) => {
              if (item.status === 'error') {
                return item
              }
            })
            .filter(Boolean) || []

        // Find the validation result for this SKU
        const validProduct = validProducts.find(
          (vp: any) => vp?.product_sku === sku,
        )
        const invalidProduct = invalidProducts.find(
          (ip: any) => ip?.product_sku === sku,
        )

        if (validProduct) {
          // Add valid product to the list
          setScannedProducts((prev) => {
            const newList = [
              ...prev,
              {
                product_sku: sku,
                pixelone_count: 0,
                physical_count: 0,
                validated: true,
                isValid: true,
                errorMessage: undefined,
                product_id: validProduct?.product_id,
                product_name: validProduct?.product_name || 'Unknown Product',
                product_image: validProduct?.product_image_path || null,
              },
            ]
            return newList
          })

          // Focus the new row's input after a brief delay
          setTimeout(() => {
            const inputs = document.querySelectorAll('input[type="number"]')
            const lastInput = inputs[inputs.length - 1] as HTMLInputElement
            if (lastInput) {
              lastInput.focus()
            }
          }, 100)

          pixelFactory.showSuccess(`Product ${sku} validated successfully!`)
        } else if (invalidProduct) {
          // Add invalid product to the list with error message
          setScannedProducts((prev) => {
            const newList = [
              ...prev,
              {
                product_sku: sku,
                physical_count: 0,
                validated: true,
                isValid: false,
                errorMessage:
                  invalidProduct.message || 'Product validation failed',
                product_id: undefined,
                product_name: 'Invalid Product',
                product_image: null,
              },
            ]
            return newList
          })

          pixelFactory.showFailure(
            `Product ${sku} validation failed: ${
              invalidProduct.message || 'Unknown error'
            }`,
          )
        } else {
          // Fallback case - add product as invalid
          setScannedProducts((prev) => {
            const newList = [
              ...prev,
              {
                product_sku: sku,
                physical_count: 0,
                validated: true,
                isValid: false,
                errorMessage: 'Product not found in validation response',
                product_id: undefined,
              },
            ]
            return newList
          })

          pixelFactory.showFailure(
            `Product ${sku} validation failed: Product not found`,
          )
        }

        // Update validation results based on current scanned products
      } else {
        throw new Error('Invalid validation response')
      }
    } catch (error) {
      console.error('Failed to validate product:', error)

      // Add product as invalid due to API error
      setScannedProducts((prev) => {
        const newList = [
          ...prev,
          {
            product_sku: sku,
            physical_count: 0,
            validated: true,
            isValid: false,
            errorMessage: 'API validation failed. Please try again.',
            product_id: undefined,
          },
        ]
        return newList
      })

      pixelFactory.showFailure(
        `Failed to validate product ${sku}. Please try again.`,
      )
    }
  }

  const handleStockInput = (index: number, physical_count: number) => {
    setScannedProducts((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, physical_count: physical_count } : item,
      ),
    )
  }

  //call on product add
  const handleProductCount = async (index: number) => {
    const product = scannedProducts[index]

    if (
      !product ||
      !product.isValid ||
      !product.product_id ||
      !cycleData.warehouseId
    ) {
      pixelFactory.showFailure(
        'Cannot process product count. Please ensure product is valid and warehouse is selected.',
      )
      return
    }

    setScannedProducts((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, countProcessed: 'processing' } : item,
      ),
    )

    setScannerModal(true)

    if (!selectedApprover) {
      pixelFactory.showFailure(
        'No approver selected. Please select an approver before adding products to the cycle.',
      )
      return
    }

    try {
      const normalizedProductId =
        typeof product.product_id === 'object'
          ? (product.product_id as any)?.product_id
          : product.product_id
      const currentProductData = {
        product_id: normalizedProductId,
        product_sku: product.product_sku,
        warehouse_id: cycleData.warehouseId,
        physical_count: product.physical_count,
        reason: product.reason || '',
      }

      if (!currentCycleId) {
        pixelFactory.showPageLoader()
        // First product - create new cycle with single product object
        const createPayload = {
          warehouse_id: cycleData.warehouseId,
          status: 'in_progress',
          product: currentProductData,
          approver_id: selectedApprover,
        }

        const response = await addProductToCycle(createPayload).toPromise()
        if (response && response.response) {
          pixelFactory.hidePageLoader()
          // Update only the current product with the response data
          const newCycle = response.response.data[0] as any
          const responseData = response.response?.data[0]
          if (responseData) {
            setScannedProducts((prev) =>
              prev.map((item, i) =>
                i === index
                  ? {
                      ...item,
                      countProcessed: true,
                      pixelone_count: responseData.pixelone_count || 0,
                      difference: responseData.unmatched_products_count,
                      errorMessage: responseData.errorMessage,
                      reason: responseData.reason,
                      detail_id: responseData.id,
                    }
                  : item,
              ),
            )
          }

          setCurrentCycleId(newCycle?.inventory_cycle_id?.id)
          setCurrentCycleStatus('in_progress')
          setCurrentCycleTotalWarehouseProducts(
            (newCycle as any)?.inventory_cycle_id?.total_warehouse_products ||
              0,
          )
          pixelFactory.showSuccess(
            `Inventory cycle #${
              newCycle.inventory_cycle_id || newCycle.id
            } created successfully!`,
          )
        }
      } else {
        // Subsequent products - call create endpoint with cycle_id, sending single product object
        const createPayload = {
          warehouse_id: cycleData.warehouseId,
          cycle_id: currentCycleId,
          status: 'in_progress',
          product: currentProductData,
          approver_id: selectedApprover,
        }

        const response = await addProductToCycle(createPayload).toPromise()
        if (response && response.response) {
          // Update only the current product with the response data
          const responseData = response.response?.data
          if (responseData) {
            setScannedProducts((prev) =>
              prev.map((item, i) =>
                i === index
                  ? {
                      ...item,
                      countProcessed: true,
                      difference: responseData.unmatched_products_count,
                      errorMessage: responseData.errorMessage,
                      pixelone_count: responseData.pixelone_count,
                      reason: responseData.reason,
                      detail_id: responseData.id,
                    }
                  : item,
              ),
            )
          }
          pixelFactory.showSuccess(
            `Product ${product.product_sku} added to cycle #${currentCycleId}`,
          )
        }
      }
    } catch (error) {
      console.error('Failed to process product:', error)

      // Mark product as failed and show error
      setScannedProducts((prev) =>
        prev.map((item, i) =>
          i === index
            ? {
                ...item,
                countProcessed: 'failed',
                errorMessage: 'Product processing failed',
              }
            : item,
        ),
      )

      // Show error message in background
      console.error(`Failed to process product ${product.product_sku}:`, error)
    }
  }
  //call on start cycle
  const handleStartCycle = async () => {
    setCycleModal(false)
    pixelFactory.showPageLoader()
    if (!cycleData.warehouseId) {
      pixelFactory.showFailure(
        'No warehouse selected. Please select a warehouse first.',
      )
      return
    }

    // Check if all valid products have physical counts and pixelone counts

    const validProducts = scannedProducts.filter((product) => product.isValid)

    if (validProducts.length === 0) {
      pixelFactory.showFailure(
        'No valid products found. Please scan at least one product before starting the cycle.',
      )
      return
    }

    if (!currentCycleId) {
      pixelFactory.showFailure(
        'No inventory cycle found. Please process at least one product first.',
      )
      return
    }

    try {
      const allScannedProducts = scannedProducts.filter(
        (p) => (p?.physical_count || 0) > 0 || (p?.pixelone_count || 0) > 0,
      )
      const finalProducts = allScannedProducts.map((p) => ({
        product_id:
          typeof p.product_id === 'object'
            ? (p.product_id as any)?.product_id
            : (p.product_id as number),
        product_sku: p.product_sku,
        pixelone_count: p.pixelone_count || 0, // This should come from the product data
        physical_count: p.physical_count,
        discripancy: p.difference || 0, // This should be calculated
        reason: p.reason || '',
      }))

      const createPayload = {
        warehouse_id: cycleData.warehouseId,
        cycle_id: currentCycleId,
        products: finalProducts,
        comment: 'Physical inventory count',
        status: 'draft',
        approver_id: selectedApprover,
      }

      const response = await createInventoryCycle(createPayload).toPromise()
      if (response && response.response) {
        const rawData = (response.response as any).data as any
        setCurrentCycleStatus('draft')

        // Normalize possible API shapes
        const firstItem = Array.isArray(rawData) ? rawData[0] : rawData
        const cycleObj =
          firstItem?.inventory_cycle_id || firstItem?.inventory_cycle || null
        const cycleIdFromResponse = cycleObj?.id

        // Update table: if cycle exists, update row; otherwise, prepend new row
        if (cycleIdFromResponse != null) {
          const responseIdStr = String(cycleIdFromResponse)
          setInventoryCycles((prev) => {
            const exists = prev.some(
              (cycle) => String((cycle as any).id) === responseIdStr,
            )
            return exists
              ? prev.map((cycle) => {
                  return String((cycle as any).id) === responseIdStr
                    ? (cycleObj as any)
                    : cycle
                })
              : [cycleObj as any, ...prev]
          })
        }

        // Prepare details array for the slider
        const detailArray = Array.isArray(rawData)
          ? rawData
          : Array.isArray(rawData?.details)
          ? rawData.details
          : [firstItem]

        setScannerModal(false)
        setCycleDetailData({
          ...cycleDetailData,
          info: cycleObj || cycleDetailData.info,
          data: detailArray,
        })

        setCurrentCycleId(cycleIdFromResponse)
        setSelectedCycle(true)
        setDetailSlider(true)
        pixelFactory.showSuccess(
          'Inventory cycle started successfully! Status changed to draft.',
        )
      }
    } catch (error) {
      console.error('Failed to start cycle:', error)
      pixelFactory.showFailure('Failed to start cycle. Please try again.')
    } finally {
      pixelFactory.hidePageLoader()
    }
  }

  const handlePageChange = (page: number) => {
    setFilters({ ...filters, page: page - 1 })
  }

  const handlePerRowsChange = (newPerPage: number) => {
    setFilters({ ...filters, page_size: newPerPage })
  }

  // Check if all products have valid reasons for differences
  const canSaveAsDraft = React.useMemo(() => {
    if (scannedProducts.length === 0) return false

    // Check if any product has a difference but no reason
    const hasInvalidProducts = scannedProducts.some((product) => {
      if (!product.isValid) return false // Skip invalid products

      // Calculate difference between physical count and pixelone count
      const calculatedDifference = Math.abs(
        (product.physical_count || 0) - (product.pixelone_count || 0),
      )

      // If there's a difference (either from API or calculated), reason must be provided
      const hasDifference =
        (product.difference || 0) > 0 || calculatedDifference > 0
      const hasReason = product.reason && product.reason.trim() !== ''

      return hasDifference && !hasReason
    })

    return !hasInvalidProducts
  }, [scannedProducts])

  const updateInventoryCycleDetailItem = async (
    product: IInventoryCycleDetail,
    index: number,
  ) => {
    const detailId = product.detail_id

    if (!currentCycleId || !detailId) {
      pixelFactory.showFailure('Missing cycle/detail id')
      return
    }
    try {
      pixelFactory.showPageLoader()
      const body = {
        physical_count: product.physical_count,
        warehouse_id: String(cycleData.warehouseId || ''),
        product_id: String(product.product_id || ''),
      }
      const res = await updateInventoryCycleDetail(
        currentCycleId,
        detailId,
        body,
      ).toPromise()

      const responseData = (res as any).response?.data
      const detailObj = responseData?.detail || responseData
      const info =
        detailObj?.inventory_cycle_id || responseData?.inventory_cycle_id

      // Update local product row
      setScannedProducts((prev) =>
        prev.map((item, i) =>
          i === index
            ? {
                ...item,
                physical_count:
                  responseData?.physical_count ?? item.physical_count,
                pixelone_count:
                  responseData?.pixelone_count ?? item.pixelone_count,
                difference: responseData?.unmatched_products_count ?? 0,
                reason: responseData?.reason_for_discrepancy ?? item.reason,
                isEditing: false,
              }
            : item,
        ),
      )

      // Update cycles list entry if present
      if (info?.id) {
        setInventoryCycles((prev) =>
          prev.map((c) => (c.id === info.id ? info : c)),
        )
      }

      // Update cycle detail info block if we have it
      setCycleDetailData((prev) => ({
        ...prev,
        info: info || prev.info,
      }))

      pixelFactory.showSuccess('Inventory cycle detail updated successfully')
    } catch (e) {
      pixelFactory.showFailure('Failed to update inventory cycle detail')
    } finally {
      pixelFactory.hidePageLoader()
    }
  }

  const columns = [
    {
      dataKey: 'id',
      name: 'ID',
      width: isMobile ? '15%' : '5%',
      sortable: true,
      selector: (row: IInventoryCycle) => (
        <PixelText
          copyToClipboard
          style={{ fontSize: isMobile ? '12px' : '14px' }}
        >
          {row?.id}
        </PixelText>
      ),
    },
    {
      dataKey: 'wh_id.name',
      name: 'Warehouse',
      width: isMobile ? '25%' : '17%',
      sortable: true,
      selector: (row: IInventoryCycle) => (
        <DcFlexBox flexDirection="column" gap={isMobile ? '2px' : '5px'}>
          <PixelText
            style={{
              fontWeight: 'bold',
              fontSize: isMobile ? '11px' : '14px',
            }}
          >
            {row.warehouse.name}
          </PixelText>
          {!isMobile && (
            <PixelText style={{ fontSize: '12px', color: 'gray' }}>
              {row.warehouse.warehouse_code}
            </PixelText>
          )}
        </DcFlexBox>
      ),
    },
    {
      dataKey: 'total_warehouse_products',
      name: isMobile ? 'Total' : 'Warehouse Products',
      width: isMobile ? '15%' : '12%',
      sortable: true,
      selector: (row: IInventoryCycle) => (
        <PixelText
          style={{
            fontWeight: 'bold',
            fontSize: isMobile ? '12px' : '14px',
          }}
        >
          {(row as any).total_warehouse_products || 0}
        </PixelText>
      ),
    },
    {
      dataKey: 'total_products',
      name: isMobile ? 'Products' : 'Total Products',
      width: isMobile ? '15%' : '10%',
      sortable: true,
      selector: (row: IInventoryCycle) => (
        <PixelText style={{ fontSize: isMobile ? '12px' : '14px' }}>
          {row.total_products}
        </PixelText>
      ),
    },
    {
      dataKey: 'total_matched_products_count',
      name: 'Matched',
      width: isMobile ? '15%' : '8%',
      sortable: true,
      selector: (row: IInventoryCycle) => (
        <PixelText
          style={{
            color: 'green',
            fontWeight: 'bold',
            fontSize: isMobile ? '12px' : '14px',
          }}
        >
          {row.total_matched_products_count || 0}
        </PixelText>
      ),
    },
    {
      dataKey: 'total_unmatched_products_count',
      name: 'Unmatched',
      width: isMobile ? '15%' : '8%',
      sortable: true,
      selector: (row: IInventoryCycle) => (
        <PixelText
          style={{
            color: 'red',
            fontWeight: 'bold',
            fontSize: isMobile ? '12px' : '14px',
          }}
        >
          {row.total_unmatched_products_count || 0}
        </PixelText>
      ),
    },
    ...(isMobile
      ? []
      : [
          {
            dataKey: 'approver_id',
            name: 'Approver',
            width: '12%',
            sortable: true,
            selector: (row: IInventoryCycle) => (
              <DcFlexBox flexDirection="column" gap="2px">
                <PixelText style={{ fontSize: '12px', fontWeight: 'bold' }}>
                  {users.find((user) => user.id === (row as any)?.approver_id)
                    ?.name || 'Unknown'}
                </PixelText>
                <PixelText style={{ fontSize: '10px', color: 'gray' }}>
                  ID: {(row as any)?.approver_id || 'N/A'}
                </PixelText>
              </DcFlexBox>
            ),
          },
        ]),
    {
      dataKey: 'status',
      name: 'Status',
      width: isMobile ? '20%' : '10%',
      sortable: true,
      selector: (row: IInventoryCycle) => (
        <DcTag
          customBackgroundColor={
            row.status === 'completed'
              ? colors.successLight
              : row.status === 'in_progress'
              ? colors.primaryLight
              : colors.warningLight
          }
          textColor={
            row.status === 'completed'
              ? colors.success
              : row.status === 'in_progress'
              ? colors.primary
              : colors.warning
          }
        >
          {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
        </DcTag>
      ),
    },
    ...(isMobile
      ? []
      : [
          {
            dataKey: 'created_at',
            name: 'Created',
            width: '12%',
            sortable: true,
            selector: (row: IInventoryCycle) => (
              <PixelText style={{ fontSize: '12px' }}>
                <PixelDate
                  format={'dateWithTime'}
                  value={row?.created_at?.date}
                  className={''}
                />
              </PixelText>
            ),
          },
        ]),
  ]

  return (
    <>
      <PixelPageContainer
        style={{ maxHeight: '100vh', height: '100vh', padding: '0px' }}
      >
        <SectionContent style={{ padding: '0px', borderRadius: '0px' }}>
          <DcFlexBox
            height="60px"
            style={{
              fontSize: '16px',
              padding: '8px 0px',
              borderBottom: `1px solid ${colors.grayBorderLight}`,
            }}
          >
            <DcFlexBox
              justifyContent="space-between"
              alignItems="center"
              style={{ padding: '.5rem 20px' }}
            >
              <DcBreadcrumbs data={breadcrumbs} />
              <ProfileSection />
            </DcFlexBox>
          </DcFlexBox>
        </SectionContent>

        <SectionContent style={{ padding: isMobile ? '10px' : '5px 20px' }}>
          <DcFlexBox
            justifyContent="space-between"
            alignItems="center"
            flexDirection={isMobile ? 'column' : 'row'}
            gap={isMobile ? '10px' : '0px'}
          >
            {!isMobile ? (
              <>
                <DcFlexBox
                  style={{
                    padding: '0px',
                    width: isMobile ? '100%' : '50%',
                  }}
                  gap="5px"
                  alignItems="center"
                  flexDirection={isMobile ? 'column' : 'row'}
                >
                  <DcDropDown
                    dropdownSize="sm"
                    placeholder="Select Warehouse"
                    options={[
                      { label: 'All Warehouses', value: 'all' },
                      ...(warehousesList || []).map((w: IWarehouse) => ({
                        label: w.name,
                        value: w.wh_id,
                      })),
                    ]}
                    value={filters.warehouse_id || 'all'}
                    onChange={(e) => {
                      setFilters({
                        ...filters,
                        warehouse_id:
                          e.target.value === 'all'
                            ? null
                            : Number(e.target.value),
                      })
                    }}
                  />
                  <DcDropDown
                    dropdownSize="sm"
                    placeholder="Status"
                    options={[
                      { label: 'All Status', value: 'all' },
                      { label: 'Draft', value: 'draft' },
                      { label: 'Approved', value: 'approved' },
                      { label: 'Rejected', value: 'rejected' },
                    ]}
                    value={filters.status}
                    onChange={(e) => {
                      setFilters({
                        ...filters,
                        status: e.target.value,
                      })
                    }}
                  />

                  <DcInput
                    inputSize="small"
                    placeholder="Search"
                    value={filters.search}
                    onChange={(e) => {
                      setFilters({
                        ...filters,
                        search: e.target.value,
                      })
                    }}
                    name={''}
                  />
                </DcFlexBox>
                <DcFlexBox
                  width={isMobile ? '100%' : '30%'}
                  justifyContent={isMobile ? 'center' : 'flex-end'}
                  gap="5px"
                >
                  <DcButton
                    size="sm"
                    variant="outline-primary"
                    width={isMobile ? '100%' : 'auto'}
                    onClick={() => {
                      setScannerModal(false)
                      setCycleModal(true)
                      //make the start cycle rows empty
                      setScannedProducts([])
                      setCurrentCycleId(null)
                      setCurrentCycleStatus('')
                      setCurrentCycleTotalWarehouseProducts(0)
                    }}
                    // disabled={!filters.warehouse_id}
                  >
                    Start Cycle
                  </DcButton>
                </DcFlexBox>
              </>
            ) : (
              <DcFlexBox position="relative" height="auto" gap="5px">
                <DcInput
                  placeholder="Search"
                  value={filters.search}
                  onChange={(e) => {
                    setFilters({
                      ...filters,
                      search: e.target.value,
                    })
                  }}
                  name={''}
                />
                <DcDiv position="absolute" right="0px" top="2px">
                  <DcButton
                    variant="link"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <FilterLinesIcon size={16} />
                  </DcButton>
                </DcDiv>
              </DcFlexBox>
            )}
          </DcFlexBox>
        </SectionContent>
        <Section style={{ padding: '20px' }}>
          <SectionContent>
            <PixelTable
              columns={columns}
              data={inventoryCycles}
              pagination={true}
              paginationServer
              progressPending={tableLoading}
              highlightOnHover={true}
              pointerOnHover={true}
              onRowClicked={async (row) => {
                if (row.status === 'in_progress') {
                  setLoading(true)
                  // Load details of the existing in_progress cycle
                  setCycleModal(true)

                  try {
                    const response = await getInventoryCycleDetails(
                      row.id,
                    ).toPromise()
                    if (response && response.response) {
                      const cycleDetails = response.response.data as any
                      setCurrentCycleId(row.id)
                      setCurrentCycleStatus(row.status)
                      setCurrentCycleTotalWarehouseProducts(
                        (row as any).total_warehouse_products || 0,
                      )

                      // Auto-select approver from the cycle data
                      if ((row as any).approver_id) {
                        setSelectedApprover((row as any).approver_id)
                      }

                      // Check if products exist and handle different response structures
                      let existingProducts: any[] = []

                      if (cycleDetails[0].inventory_cycle_id) {
                        // API response has 'details' array with product information
                        existingProducts = cycleDetails.map((detail: any) => ({
                          product_sku: detail.product_sku,
                          physical_count: detail.physical_count || 0,
                          isValid: true,
                          errorMessage: undefined,
                          product_id:
                            typeof detail.product_id === 'object'
                              ? detail.product_id?.product_id
                              : detail.product_id,
                          product_name:
                            detail.product_id?.product_name ||
                            'Unknown Product',
                          product_image:
                            detail.product_id?.product_image_path || null,
                          countProcessed: true,
                          pixelone_count: detail.pixelone_count || 0,
                          difference: detail.unmatched_products_count || 0,
                          reason: detail.reason_for_discrepancy || '',
                          detail_id: detail.id,
                          isEditing: false,
                        }))
                      } else {
                        pixelFactory.showWarning(
                          'No existing products found in this cycle. Starting fresh.',
                        )
                      }

                      setScannedProducts(existingProducts)

                      // Open the cycle modal to continue working
                      setCycleModal(true)
                      setScannerModal(false)

                      pixelFactory.showSuccess(
                        `Loaded existing cycle #${row.id} with ${existingProducts.length} products. You can add more products.`,
                      )
                    }
                  } catch (error) {
                    pixelFactory.showFailure(
                      'Failed to load cycle details. Please try again.',
                    )
                  } finally {
                    setLoading(false)
                  }
                } else {
                  handleViewDetails(row)
                }
              }}
              fixedHeader
              fixedHeaderScrollHeight={
                isMobile ? 'calc(100vh - 240px)' : 'calc(100vh - 216px)'
              }
              paginationPerPage={filters.page_size}
              onChangeRowsPerPage={handlePerRowsChange}
              onChangePage={handlePageChange}
              paginationTotalRows={totalRows}
            />
          </SectionContent>
        </Section>
      </PixelPageContainer>

      {/* Scanner Modal */}
      <DcOverlaySlider
        isOpen={cycleModal}
        onClose={() => {
          setCycleModal(false)
          if (scannedProducts.length > 0) {
            loadInventoryCycles()
          }
        }}
        title="Scan Products"
        width={isMobile ? '95%' : '70%'}
        loading={loading}
      >
        <SectionContent style={{ padding: '20px' }}>
          <DcFlexBox flexDirection="column" gap="20px">
            {/* Scanner Controls */}
            <DcFlexBox
              justifyContent="space-between"
              alignItems="center"
              flexDirection={isMobile ? 'column' : 'row'}
              gap={isMobile ? '15px' : '0px'}
            >
              <DcFlexBox flexDirection="column" gap="10px">
                <PixelText
                  style={{
                    fontSize: isMobile ? '16px' : '18px',
                    fontWeight: 'bold',
                    textAlign: isMobile ? 'center' : 'left',
                  }}
                >
                  Scan Products and Enter Physical Counts
                </PixelText>
                {currentCycleId && (
                  <DcFlexBox
                    alignItems="center"
                    gap="10px"
                    flexDirection={isMobile ? 'column' : 'row'}
                    style={{ textAlign: isMobile ? 'center' : 'left' }}
                  >
                    <PixelText
                      style={{
                        fontSize: isMobile ? '12px' : '14px',
                        color: colors.grayLight,
                      }}
                    >
                      Cycle #{currentCycleId} - Status: {currentCycleStatus}
                    </PixelText>
                    {currentCycleStatus === 'in_progress' && (
                      <DcFlexBox
                        width="auto"
                        alignItems="center"
                        gap="5px"
                        flexDirection={isMobile ? 'column' : 'row'}
                      >
                        <PixelText
                          style={{
                            fontSize: isMobile ? '10px' : '12px',
                            color: colors.grayLight,
                          }}
                        >
                          ✓ Auto-updating cycle
                        </PixelText>
                        <PixelText
                          style={{
                            fontSize: isMobile ? '10px' : '12px',
                            color: colors.grayLight,
                          }}
                        >
                          ({scannedProducts.length} products loaded)
                        </PixelText>
                      </DcFlexBox>
                    )}
                  </DcFlexBox>
                )}
              </DcFlexBox>
              <DcFlexBox
                gap="10px"
                alignItems="center"
                width={isMobile ? '100%' : 'auto'}
                flexDirection={isMobile ? 'column' : 'row'}
              >
                <DcDiv width={isMobile ? '100%' : '250px'}>
                  <DcDropDown
                    dropdownSize="sm"
                    options={warehousesList?.map((w: IWarehouse) => ({
                      label: w.name,
                      value: w.wh_id,
                    }))}
                    value={cycleData.warehouseId || 'all'}
                    onChange={(e) => {
                      setCycleData({
                        ...cycleData,
                        warehouseId:
                          e.target.value === 'all'
                            ? null
                            : Number(e.target.value),
                      })
                    }}
                    placeholder="Select Warehouse"
                  />
                </DcDiv>
                <DcDiv width={isMobile ? '100%' : '250px'}>
                  <DcDropDown
                    dropdownSize="sm"
                    options={[
                      { label: 'Select Approver', value: '' },
                      ...users.map((user) => ({
                        label: user.name,
                        value: user.id.toString(),
                      })),
                    ]}
                    value={selectedApprover?.toString() || ''}
                    onChange={(e) => {
                      setSelectedApprover(
                        e.target.value === '' ? null : Number(e.target.value),
                      )
                    }}
                    placeholder="Select Approver"
                  />
                </DcDiv>
                <DcButton
                  size="sm"
                  variant="outline-success"
                  width={isMobile ? '100%' : 'auto'}
                  onClick={() => {
                    setScannerModal(true)
                  }}
                  withIcon={true}
                >
                  <ScanIcon size={16} />
                  Scan Products
                </DcButton>
              </DcFlexBox>
            </DcFlexBox>

            {/* Camera Scanner */}
            <DcScanner
              isOpen={scannerModal}
              onClose={() => setScannerModal(false)}
              scannedSKU={scannedSKU}
              setScannedSKU={setScannedSKU}
              handleScan={handleScan}
            />
            <DcFlexBox
              flexDirection="column"
              gap="20px"
              width="100%"
              alignItems="center"
            >
              {/* Scanned Products List */}
              {scannedProducts.length > 0 ? (
                <DcFlexBox flexDirection="column" gap="15px">
                  <DcFlexBox
                    justifyContent="space-between"
                    height="auto"
                    flexDirection={isMobile ? 'column' : 'row'}
                    gap={isMobile ? '10px' : '0px'}
                    alignItems={isMobile ? 'center' : 'flex-start'}
                  >
                    <PixelText
                      style={{
                        fontSize: isMobile ? '14px' : '16px',
                        fontWeight: 'bold',
                        textAlign: isMobile ? 'center' : 'left',
                      }}
                    >
                      Scanned Products ({scannedProducts.length})
                    </PixelText>
                    <PixelText
                      style={{
                        fontSize: isMobile ? '12px' : '16px',
                        fontWeight: 'bold',
                        textAlign: isMobile ? 'center' : 'right',
                      }}
                    >
                      Remaining Products:{' '}
                      {Number(currentCycleTotalWarehouseProducts) -
                        Number(scannedProducts.length)}
                    </PixelText>
                  </DcFlexBox>
                  <DcDiv
                    width="100%"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: isMobile ? '10px' : '15px',
                      maxHeight: isMobile
                        ? 'calc(100vh - 585px)'
                        : 'calc(100vh - 260px)',
                      overflow: 'auto',
                    }}
                  >
                    {scannedProducts.map((product, index) => (
                      <DcFlexBox
                        key={index}
                        justifyContent="space-between"
                        alignItems={isMobile ? 'flex-start' : 'center'}
                        padding={isMobile ? '10px' : '15px'}
                        flexDirection={isMobile ? 'column' : 'row'}
                        gap={isMobile ? '10px' : '0px'}
                        style={{
                          border: product.isValid
                            ? `1px solid ${colors.grayBorderLight}`
                            : `1px solid ${colors.dangerLight}`,
                          borderRadius: '8px',
                          backgroundColor: product.isValid
                            ? product.physical_count > 0
                              ? colors.grayLight
                              : colors.dangerLight
                            : colors.dangerLight,
                        }}
                      >
                        <DcFlexBox
                          width={isMobile ? '100%' : '35%'}
                          alignItems="center"
                          gap={isMobile ? '10px' : '15px'}
                          flexDirection="row"
                        >
                          {/* Product Image */}
                          {product.product_image && (
                            <PixelImage
                              src={product.product_image}
                              height={isMobile ? '40px' : '50px'}
                              width={isMobile ? '40px' : '50px'}
                              style={{
                                borderRadius: '8px',
                                objectFit: 'cover',
                                border: `1px solid ${colors.grayBorderLight}`,
                              }}
                            />
                          )}
                          <DcFlexBox
                            flexDirection="column"
                            gap={isMobile ? '3px' : '5px'}
                            style={{ textAlign: isMobile ? 'center' : 'left' }}
                          >
                            <PixelText
                              style={{
                                fontSize: isMobile ? '14px' : '16px',
                                fontWeight: 'bold',
                                color: product.isValid
                                  ? colors.grayDarker
                                  : colors.danger,
                              }}
                            >
                              {product.product_name}
                            </PixelText>
                            <PixelText
                              style={{
                                fontSize: isMobile ? '12px' : '14px',
                                color: product.isValid
                                  ? colors.grayMedium
                                  : colors.danger,
                                fontWeight: product.isValid ? 'normal' : 'bold',
                              }}
                            >
                              SKU: {product.product_sku}
                            </PixelText>

                            {!product.isValid && product.errorMessage && (
                              <PixelText
                                style={{
                                  fontSize: '12px',
                                  color: colors.danger,
                                  fontStyle: 'italic',
                                }}
                              >
                                Error: {product.errorMessage}
                              </PixelText>
                            )}
                          </DcFlexBox>
                        </DcFlexBox>

                        <DcFlexBox
                          flexDirection={isMobile ? 'column' : 'row'}
                          width={isMobile ? '100%' : '60% '}
                          alignItems="center"
                          gap="15px"
                        >
                          {product.isValid ? (
                            <>
                              {!product.countProcessed ? (
                                <>
                                  <DcFlexBox flexDirection="row" gap="5px">
                                    <PixelText
                                      style={{
                                        width: isMobile ? '105px' : 'auto',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                      }}
                                    >
                                      New Count:
                                    </PixelText>
                                    <form
                                      action=""
                                      onSubmit={(e) => {
                                        e.preventDefault()
                                        handleProductCount(index)
                                      }}
                                    >
                                      <DcInput
                                        type="number"
                                        value={product.physical_count || 0}
                                        onChange={(e) => {
                                          handleStockInput(
                                            index,
                                            Number(e.target.value) || 0,
                                          )
                                          setCanStartCycle(true)
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault()
                                            handleProductCount(index)
                                          }
                                        }}
                                        placeholder="Enter physical count"
                                        name=""
                                      />
                                    </form>
                                  </DcFlexBox>

                                  <DcFlexBox height="auto" gap="5px">
                                    <DcFlexBox gap="5px">
                                      <PixelText
                                        style={{
                                          fontSize: '14px',
                                          color: 'gray',
                                        }}
                                      >
                                        Status
                                      </PixelText>
                                      <PixelText
                                        style={{
                                          fontSize: '14px',
                                          color:
                                            product.physical_count > 0
                                              ? colors.success
                                              : colors.danger,
                                          fontWeight: 'bold',
                                        }}
                                      >
                                        {product.physical_count > 0
                                          ? '✓ Stock Entered'
                                          : '⚠ No Stock'}
                                      </PixelText>
                                    </DcFlexBox>
                                    <DcFlexBox
                                      width="auto"
                                      flexDirection="column"
                                      gap="5px"
                                    >
                                      <DcButton
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={() =>
                                          handleProductCount(index)
                                        }
                                      >
                                        Enter
                                      </DcButton>
                                    </DcFlexBox>
                                  </DcFlexBox>
                                </>
                              ) : product.countProcessed === 'processing' ? (
                                <DcFlexBox
                                  flexDirection="column"
                                  gap="5px"
                                  alignItems="center"
                                >
                                  <PixelText
                                    style={{
                                      fontSize: '14px',
                                      color: colors.primary,
                                      fontStyle: 'italic',
                                    }}
                                  >
                                    🔄 Processing Count...
                                  </PixelText>
                                  <PixelText
                                    style={{
                                      fontSize: '12px',
                                      color: colors.grayMedium,
                                    }}
                                  >
                                    Continue scanning other products
                                  </PixelText>
                                </DcFlexBox>
                              ) : product.countProcessed === 'failed' ? (
                                <DcFlexBox
                                  flexDirection="column"
                                  gap="5px"
                                  alignItems="center"
                                >
                                  <PixelText
                                    style={{
                                      fontSize: '14px',
                                      color: colors.danger,
                                      fontWeight: 'bold',
                                    }}
                                  >
                                    ❌ Count Failed
                                  </PixelText>
                                  <DcFlexBox gap="10px" alignItems="center">
                                    <DcButton
                                      size="sm"
                                      variant="outline-danger"
                                      onClick={() => handleProductCount(index)}
                                      withIcon={true}
                                    >
                                      <CheckIcon size={14} />
                                      Retry
                                    </DcButton>
                                    <DcButton
                                      size="sm"
                                      variant="outline-secondary"
                                      onClick={() =>
                                        handleRemoveInvalidProduct(
                                          product.product_sku,
                                        )
                                      }
                                      withIcon={true}
                                    >
                                      <CloseIcon size={14} />
                                      Remove
                                    </DcButton>
                                  </DcFlexBox>
                                </DcFlexBox>
                              ) : (
                                <>
                                  <DcFlexBox
                                    flexDirection="column"
                                    alignItems="center"
                                    gap="10px"
                                  >
                                    <DcFlexBox alignItems="center" gap="15px">
                                      <PixelText
                                        style={{
                                          fontSize: '14px',
                                          fontWeight: 'bold',
                                        }}
                                      >
                                        Old Count:{' '}
                                        {product?.pixelone_count || 0}
                                      </PixelText>
                                      <PixelText
                                        style={{
                                          fontSize: '14px',
                                          fontWeight: 'bold',
                                        }}
                                      >
                                        New Count: {product.physical_count}
                                      </PixelText>
                                      <PixelText
                                        style={{
                                          fontSize: '14px',
                                          fontWeight: 'bold',
                                          color: product?.difference
                                            ? colors.danger
                                            : colors.success,
                                        }}
                                      >
                                        Difference: {product.difference ?? 0}
                                      </PixelText>
                                    </DcFlexBox>
                                    {product.difference
                                      ? !product.isEditing && (
                                          <DcFlexBox
                                            alignItems="center"
                                            gap="10px"
                                          >
                                            <PixelText
                                              style={{ fontSize: '14px' }}
                                            >
                                              Reason:
                                            </PixelText>
                                            <DcInput
                                              type="text"
                                              name="reason"
                                              value={product.reason || ''}
                                              onChange={(e) => {
                                                setScannedProducts((prev) =>
                                                  prev.map((item, i) =>
                                                    i === index
                                                      ? {
                                                          ...item,
                                                          reason:
                                                            e.target.value,
                                                        }
                                                      : item,
                                                  ),
                                                )
                                              }}
                                              placeholder="Enter reason for difference"
                                              style={{ width: '200px' }}
                                            />
                                          </DcFlexBox>
                                        )
                                      : ''}
                                  </DcFlexBox>
                                  {product.isEditing ? (
                                    <DcFlexBox gap="10px" alignItems="center">
                                      <form
                                        action=""
                                        onSubmit={(e) => {
                                          e.preventDefault()
                                          updateInventoryCycleDetailItem(
                                            product,
                                            index,
                                          )
                                        }}
                                      >
                                        <DcInput
                                          type="number"
                                          value={
                                            product.physical_count === 0
                                              ? ''
                                              : product.physical_count ?? ''
                                          }
                                          onChange={(e) => {
                                            const raw = String(
                                              e.target.value ?? '',
                                            )
                                            const sanitized = raw.replace(
                                              /^0+(?=\d)/,
                                              '',
                                            )
                                            const value =
                                              sanitized === ''
                                                ? 0
                                                : Number(sanitized)
                                            setScannedProducts((prev) =>
                                              prev.map((item, i) =>
                                                i === index
                                                  ? {
                                                      ...item,
                                                      physical_count: value,
                                                    }
                                                  : item,
                                              ),
                                            )
                                          }}
                                          placeholder="Enter new physical count"
                                          style={{ width: '140px' }}
                                          name={'enter_new_physical_count'}
                                        />
                                      </form>
                                      <DcButton
                                        size="sm"
                                        variant="link"
                                        type="submit"
                                        onClick={() =>
                                          //call function here to update the inventory cycle detail
                                          updateInventoryCycleDetailItem(
                                            product,
                                            index,
                                          )
                                        }
                                      >
                                        <SaveIcon size={16} />
                                      </DcButton>
                                      <DcButton
                                        size="sm"
                                        variant="link"
                                        onClick={() =>
                                          setScannedProducts((prev) =>
                                            prev.map((item, i) =>
                                              i === index
                                                ? { ...item, isEditing: false }
                                                : item,
                                            ),
                                          )
                                        }
                                      >
                                        <CloseIcon size={16} />
                                      </DcButton>
                                    </DcFlexBox>
                                  ) : (
                                    <DcButton
                                      variant="link"
                                      size="sm"
                                      style={{ marginTop: '22px' }}
                                      onClick={() =>
                                        setScannedProducts((prev) =>
                                          prev.map((item, i) =>
                                            i === index
                                              ? { ...item, isEditing: true }
                                              : item,
                                          ),
                                        )
                                      }
                                    >
                                      <PenIcon size={16} />
                                    </DcButton>
                                  )}
                                </>
                              )}
                            </>
                          ) : (
                            <DcFlexBox
                              flexDirection="column"
                              gap="5px"
                              alignItems="center"
                            >
                              <PixelText
                                style={{
                                  fontSize: '14px',
                                  color: colors.danger,
                                  fontWeight: 'bold',
                                }}
                              >
                                ❌ Invalid Product
                              </PixelText>
                              <DcButton
                                size="sm"
                                variant="outline-danger"
                                onClick={() =>
                                  handleRemoveInvalidProduct(
                                    product.product_sku,
                                  )
                                }
                                withIcon={true}
                              >
                                <CloseIcon size={14} />
                                Remove
                              </DcButton>
                            </DcFlexBox>
                          )}
                        </DcFlexBox>
                      </DcFlexBox>
                    ))}
                  </DcDiv>
                  <DcFlexBox
                    flexDirection={isMobile ? 'column' : 'row-reverse'}
                    gap={isMobile ? '10px' : '0px'}
                  >
                    <DcFlexBox
                      width={isMobile ? '100%' : '50%'}
                      justifyContent={isMobile ? 'center' : 'flex-end'}
                      gap="15px"
                      flexDirection={isMobile ? 'column' : 'row'}
                    >
                      {scannedProducts.some((product) => !product.isValid) && (
                        <DcButton
                          size="sm"
                          variant="outline-danger"
                          width={isMobile ? '100%' : 'auto'}
                          onClick={() => {
                            setScannedProducts((prev) =>
                              prev.filter((product) => product.isValid),
                            )
                          }}
                          withIcon={true}
                        >
                          <CloseIcon size={16} />
                          Remove Invalid (
                          {scannedProducts.filter((p) => !p.isValid).length})
                        </DcButton>
                      )}

                      <DcButton
                        size="sm"
                        withIcon={true}
                        variant="outline-primary"
                        width={isMobile ? '100%' : 'auto'}
                        onClick={handleStartCycle}
                        disabled={!canSaveAsDraft}
                        title={
                          !canSaveAsDraft
                            ? 'Please provide reasons for all products with differences before saving as draft'
                            : 'Save current cycle as draft'
                        }
                      >
                        <SaveIcon size={16} />
                        Save as Draft
                      </DcButton>
                    </DcFlexBox>

                    {/* Validation Status */}
                    {scannedProducts.length > 0 && (
                      <DcFlexBox
                        width={isMobile ? '100%' : '50%'}
                        justifyContent={isMobile ? 'center' : 'flex-start'}
                        flexDirection="column"
                        gap="5px"
                        style={{ textAlign: isMobile ? 'center' : 'left' }}
                      >
                        <PixelText
                          style={{
                            fontSize: '14px',
                            color: canStartCycle
                              ? colors.success
                              : colors.warning,
                            fontWeight: 'bold',
                          }}
                        >
                          {canStartCycle
                            ? '✓ All products validated! Click "Start Cycle" to continue.'
                            : '⚠ Please enter physical counts for all scanned products.'}
                        </PixelText>
                        {scannedProducts.some(
                          (product) => !product.isValid,
                        ) && (
                          <PixelText
                            style={{
                              fontSize: '12px',
                              color: colors.danger,
                              fontWeight: 'bold',
                            }}
                          >
                            ⚠ {scannedProducts.filter((p) => !p.isValid).length}{' '}
                            invalid products found. Remove them or fix the
                            issues.
                          </PixelText>
                        )}
                        {!canSaveAsDraft && (
                          <PixelText
                            style={{
                              fontSize: '12px',
                              color: colors.danger,
                              fontWeight: 'bold',
                            }}
                          >
                            ⚠ Some products with differences are missing
                            reasons. Please provide reasons before saving as
                            draft.
                          </PixelText>
                        )}
                      </DcFlexBox>
                    )}
                  </DcFlexBox>
                </DcFlexBox>
              ) : (
                <DcFlexBox
                  flexDirection="column"
                  alignItems="center"
                  gap="20px"
                  style={{ padding: '40px' }}
                >
                  <PixelText style={{ fontSize: '16px', color: '#6c757d' }}>
                    No products scanned yet. Use a barcode scanner or manually
                    enter SKUs above.
                  </PixelText>
                  <PixelText style={{ fontSize: '14px', color: '#adb5bd' }}>
                    Scan products to begin inventory cycle
                  </PixelText>
                </DcFlexBox>
              )}
            </DcFlexBox>
          </DcFlexBox>
        </SectionContent>
      </DcOverlaySlider>

      {/* Inventory Cycle Detail Slider */}
      <DcOverlaySlider
        isOpen={detailSlider}
        onClose={handleCloseDetail}
        title="Inventory Cycle Details"
        width={isMobile ? '95%' : '70%'}
        padding="0px"
        loading={detailLoading}
      >
        {/* <SectionContent style={{ padding: '20px' }}> */}
        {selectedCycle ? (
          <InventoryCycleDetail
            cycleId={cycleId}
            cycleDetailData={cycleDetailData}
            onClose={handleCloseDetail}
            onApprove={handleApproveCycle}
            onReject={handleRejectCycle}
            setDetailLoading={handleSetDetailLoading}
            detailLoading={detailLoading}
          />
        ) : (
          <DcFlexBox flexDirection="column" alignItems="center" gap="20px">
            <PixelText style={{ fontSize: '16px', color: 'gray' }}>
              No inventory cycle selected.
            </PixelText>
          </DcFlexBox>
        )}
        {/* </SectionContent> */}
      </DcOverlaySlider>
      <DcOverlaySlider
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        title="Filters"
        width={isMobile ? '95%' : '70%'}
        customWith="70%"
        padding="0px"
      >
        <SectionContent style={{ padding: '20px' }}>
          <DcFlexBox flexDirection="column" gap="20px">
            <DcFlexBox
              style={{
                padding: '0px',
                width: isMobile ? '100%' : '50%',
              }}
              gap="5px"
              alignItems="center"
              flexDirection={isMobile ? 'column' : 'row'}
            >
              <DcDropDown
                dropdownSize="sm"
                placeholder="Select Warehouse"
                options={[
                  { label: 'All Warehouses', value: 'all' },
                  ...(warehousesList || []).map((w: IWarehouse) => ({
                    label: w.name,
                    value: w.wh_id,
                  })),
                ]}
                value={filters.warehouse_id || 'all'}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    warehouse_id:
                      e.target.value === 'all' ? null : Number(e.target.value),
                  })
                }}
              />
              <DcDropDown
                dropdownSize="sm"
                placeholder="Status"
                options={[
                  { label: 'All Status', value: 'all' },
                  { label: 'Draft', value: 'draft' },
                  { label: 'Approved', value: 'approved' },
                  { label: 'Rejected', value: 'rejected' },
                ]}
                value={filters.status}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    status: e.target.value,
                  })
                }}
              />

              <DcInput
                inputSize="small"
                placeholder="Search"
                value={filters.search}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    search: e.target.value,
                  })
                }}
                name={''}
              />
            </DcFlexBox>
            <DcFlexBox
              width={isMobile ? '100%' : '30%'}
              justifyContent={isMobile ? 'center' : 'flex-end'}
              gap="5px"
            >
              <DcButton
                size="sm"
                variant="outline-primary"
                width={isMobile ? '100%' : 'auto'}
                onClick={() => {
                  setScannerModal(false)
                  setCycleModal(true)
                  //make the start cycle rows empty
                  setScannedProducts([])
                  setCurrentCycleId(null)
                  setCurrentCycleStatus('')
                  setCurrentCycleTotalWarehouseProducts(0)
                }}
                // disabled={!filters.warehouse_id}
              >
                Start Cycle
              </DcButton>
            </DcFlexBox>
          </DcFlexBox>
        </SectionContent>
      </DcOverlaySlider>
    </>
  )
}

export default InventoryLifecycle
